import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { MaskTitle, Reveal, SectionLabel } from "./motion";
import { localized } from "@/lib/i18n";
import { useLang } from "@/context/LangContext";

const spanFor = (i, n) => {
  if (n < 3) return "";
  const full = Math.floor(n / 5) * 5;
  if (i < full) return i % 5 === 0 ? "col-span-2 row-span-2" : "";
  const r = n - full;
  const j = i - full;
  if (r === 1) return "col-span-2 md:col-span-4";
  if (r === 2) return "col-span-2";
  if (r === 3) return j === 0 ? "col-span-2" : "";
  return "";
};

export const Gallery = ({ gallery }) => {
  const all = Array.isArray(gallery) ? gallery : [];
  const { t, lang } = useLang();
  const cats = useMemo(() => ["all", ...new Set(all.map((g) => g?.category).filter(Boolean))], [all]);
  const [cat, setCat] = useState("all");
  const [idx, setIdx] = useState(null);
  const list = cat === "all" ? all : all.filter((g) => g?.category === cat);
  const catLabel = (c) => (c === "all" ? t.gallery.all : t.categories[c] || c);
  const cap = (g) => localized(g, "caption", lang);
  if (all.length === 0) return null;

  const step = (d) => setIdx((i) => (i + d + list.length) % list.length);

  return (
    <section id="galleria" data-testid="gallery-section" className="bg-ink px-5 pb-24 md:px-10 md:pb-36">
      <div className="mx-auto max-w-[1600px] border-t border-white/10 pt-24 md:pt-36">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <SectionLabel index="05">{t.gallery.label}</SectionLabel>
            <MaskTitle key={lang} lines={[t.gallery.title[0], { text: t.gallery.title[1], className: "text-volt" }]}
              className="mt-6 font-display text-6xl font-black uppercase leading-[0.85] md:text-8xl lg:text-9xl" />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`gallery-filter-${c.toLowerCase()}`}
                className={`rounded-full px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${cat === c ? "bg-trident text-white" : "border border-white/15 text-zinc-300 hover:border-white/40"}`}>
                {catLabel(c)}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-14 grid auto-rows-[200px] grid-cols-2 gap-3 md:auto-rows-[300px] md:grid-cols-4 md:gap-4">
          {list.map((g, i) => (
            <Reveal key={g?.id || i} delay={Math.min(i * 0.05, 0.3)} className={`${spanFor(i, list.length)} min-h-0`}>
              <button onClick={() => setIdx(i)} data-testid={`gallery-item-${i}`} className="group relative h-full w-full overflow-hidden bg-surface">
                <img src={g?.image} alt={cap(g) || t.gallery.photo} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent opacity-80 transition-opacity duration-500 md:opacity-0 md:group-hover:opacity-100" />
                <span className="label-mono absolute bottom-3 left-3 text-[11px] text-white">{cap(g)}</span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={idx != null} onOpenChange={(v) => !v && setIdx(null)}>
        <DialogContent data-lenis-prevent data-testid="gallery-lightbox" className="max-w-5xl border-white/10 bg-ink p-3">
          <DialogTitle className="sr-only">{cap(list[idx]) || t.gallery.photo}</DialogTitle>
          <div className="relative h-[75vh] w-full overflow-hidden bg-surface">
            {idx != null && <img src={list[idx]?.image} alt={cap(list[idx])} className="h-full w-full object-contain" />}
            <button onClick={() => step(-1)} data-testid="lightbox-prev" aria-label={t.gallery.prev} className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-ink/70 text-white backdrop-blur hover:bg-trident"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => step(1)} data-testid="lightbox-next" aria-label={t.gallery.next} className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-ink/70 text-white backdrop-blur hover:bg-trident"><ChevronRight className="h-5 w-5" /></button>
          </div>
          <div className="flex items-center justify-between px-2 pb-1 pt-2">
            <p className="label-mono text-white">{cap(list[idx])}</p>
            <p className="label-mono text-white">{(idx ?? 0) + 1} / {list.length}</p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
